export const BILLING_INTERVALS = {
  monthly: "monthly",
  yearly: "yearly",
};

export const SUBSCRIPTION_STATUSES = {
  none: "none",
  active: "active",
  trialing: "trialing",
  pastDue: "past_due",
  canceled: "canceled",
  incomplete: "incomplete",
};

export const DEFAULT_ENTITLEMENTS = Object.freeze({
  featuredPlacement: false,
  homepageSpotlight: false,
  verifiedPortfolioBadge: false,
  portfolioVideoLimit: 3,
  premiumThemes: false,
  customProfileBanner: false,
  advancedProfileCustomization: false,
  reusableEditingInstructions: false,
  batchProjectPlanning: false,
  contentWorkflowBoard: false,
  projectOrganization: false,
  priorityCreatorProfile: false,
  analyticsLite: false,
  portfolioStatistics: false,
  earlyAccess: false,
  discountedCreatorReview: false,
  discountedPortfolioCritique: false,
  priorityProfileReview: false,
  higherFeaturedRotationPriority: false,
});

const CREATOR_PLUS_ENTITLEMENTS = {
  reusableEditingInstructions: true,
  priorityCreatorProfile: true,
  projectOrganization: true,
  earlyAccess: true,
};

const EDITOR_PRO_ENTITLEMENTS = {
  featuredPlacement: true,
  portfolioVideoLimit: 20,
  premiumThemes: true,
  customProfileBanner: true,
  analyticsLite: true,
  portfolioStatistics: true,
};

export const PLAN_ENTITLEMENTS = Object.freeze({
  free_creator: {},
  creator_plus: CREATOR_PLUS_ENTITLEMENTS,
  creator_studio: {
    ...CREATOR_PLUS_ENTITLEMENTS,
    batchProjectPlanning: true,
    contentWorkflowBoard: true,
    discountedCreatorReview: true,
  },
  coaching_starter: {},
  coaching_growth: {},
  coaching_scale: {},
  coaching_elite: {},
  coaching_max: {},
  free_editor: {
    portfolioVideoLimit: 3,
  },
  editor_pro: EDITOR_PRO_ENTITLEMENTS,
  editor_elite: {
    ...EDITOR_PRO_ENTITLEMENTS,
    homepageSpotlight: true,
    priorityProfileReview: true,
    advancedProfileCustomization: true,
    higherFeaturedRotationPriority: true,
    discountedPortfolioCritique: true,
  },
});

export const CREATOR_PLANS = Object.freeze([
  {
    id: "free_creator",
    audience: "creator",
    name: "Free Creator",
    description: "Everything needed to use Kre8.",
    billingPrices: {
      monthly: 0,
      yearly: 0,
    },
    stripeProductId: null,
    stripePriceIds: {
      monthly: null,
      yearly: null,
    },
    features: [
      "Browse editors",
      "Message editors",
      "Place unlimited orders",
      "Creator profile",
      "Connect YouTube",
      "Display YouTube videos",
      "Favorite editors",
      "Basic creator profile",
    ],
  },
  {
    id: "creator_plus",
    audience: "creator",
    name: "Creator Plus",
    description: "For creators who want to manage editing more efficiently.",
    billingPrices: {
      monthly: 900,
      yearly: 8640,
    },
    stripeProductId: "prod_placeholder_creator_plus",
    stripePriceIds: {
      monthly: "price_placeholder_creator_plus_monthly",
      yearly: "price_placeholder_creator_plus_yearly",
    },
    features: [
      "Reusable editing instruction templates",
      "Project organization tools",
      "Priority Creator badge",
      "Early access to new creator features",
    ],
  },
  {
    id: "creator_studio",
    audience: "creator",
    name: "Creator Studio",
    description: "For creators posting consistently.",
    billingPrices: {
      monthly: 1900,
      yearly: 18240,
    },
    stripeProductId: "prod_placeholder_creator_studio",
    stripePriceIds: {
      monthly: "price_placeholder_creator_studio_monthly",
      yearly: "price_placeholder_creator_studio_yearly",
    },
    features: [
      "Everything in Creator Plus",
      "Batch order creation",
      "Content workflow board",
      "Discounted Creator Review",
    ],
  },
  {
    id: "coaching_starter",
    audience: "creator",
    name: "Starter Coaching",
    description: "A light-touch coaching subscription for smaller creators.",
    billingPrices: {
      monthly: 3500,
      yearly: null,
    },
    stripeProductId: "prod_V0lbBo2IRHidsu",
    stripePriceIds: {
      monthly: "price_placeholder_coaching_starter_monthly",
      yearly: null,
    },
    features: [
      "Entry-level 1:1 coaching access",
      "Light accountability and feedback",
      "Best for smaller or newer creators",
    ],
  },
  {
    id: "coaching_growth",
    audience: "creator",
    name: "Growth Coaching",
    description: "A coaching tier for creators actively trying to grow faster.",
    billingPrices: {
      monthly: 14900,
      yearly: null,
    },
    stripeProductId: "prod_V0ldixicZyQ46j",
    stripePriceIds: {
      monthly: "price_placeholder_coaching_growth_monthly",
      yearly: null,
    },
    features: [
      "More direct 1:1 coaching support",
      "Personalized creator feedback",
      "A strong default coaching plan",
    ],
  },
  {
    id: "coaching_scale",
    audience: "creator",
    name: "Scale Coaching",
    description: "For creators treating content like a business and scaling output.",
    billingPrices: {
      monthly: 49900,
      yearly: null,
    },
    stripeProductId: "prod_V0leYYwG14NxBd",
    stripePriceIds: {
      monthly: "price_placeholder_coaching_scale_monthly",
      yearly: null,
    },
    features: [
      "Higher-touch coaching",
      "Growth, workflow, and hiring strategy",
      "Built for serious creators",
    ],
  },
  {
    id: "coaching_elite",
    audience: "creator",
    name: "Elite Advisory",
    description: "A premium monthly advisory subscription for top creators.",
    billingPrices: {
      monthly: 199900,
      yearly: null,
    },
    stripeProductId: "prod_V0lfTpHODdTmCi",
    stripePriceIds: {
      monthly: "price_placeholder_coaching_elite_monthly",
      yearly: null,
    },
    features: [
      "Highest-touch monthly access",
      "Direct strategy and business support",
      "Built for large or fast-scaling creators",
    ],
  },
  {
    id: "coaching_max",
    audience: "creator",
    name: "Max Advisory",
    description: "A custom coaching relationship priced as a share of earnings.",
    billingPrices: {
      monthly: null,
      yearly: null,
    },
    stripeProductId: null,
    stripePriceIds: {
      monthly: null,
      yearly: null,
    },
    features: [
      "Custom coaching relationship",
      "Manual qualification and pricing",
      "Priced at 6% of social media earnings",
    ],
  },
]);

export const EDITOR_PLANS = Object.freeze([
  {
    id: "free_editor",
    audience: "editor",
    name: "Free Editor",
    description: "Everything needed to get discovered and talk with creators.",
    billingPrices: {
      monthly: 0,
      yearly: 0,
    },
    stripeProductId: null,
    stripePriceIds: {
      monthly: null,
      yearly: null,
    },
    features: [
      "Public editor profile",
      "Creator messaging",
      "3 portfolio videos",
      "Basic profile customization",
      "Discoverable in search",
    ],
  },
  {
    id: "editor_pro",
    audience: "editor",
    name: "Editor Pro",
    description: "Best for editors who want more clients and a stronger profile.",
    billingPrices: {
      monthly: 1499,
      yearly: 14390,
    },
    stripeProductId: "prod_placeholder_editor_pro",
    stripePriceIds: {
      monthly: "price_placeholder_editor_pro_monthly",
      yearly: "price_placeholder_editor_pro_yearly",
    },
    features: [
      "20 portfolio videos",
      "Premium profile banner",
      "Premium profile themes",
      "Featured Editor badge",
      "Higher search placement eligibility",
      "Analytics Lite",
      "Portfolio Statistics",
    ],
  },
  {
    id: "editor_elite",
    audience: "editor",
    name: "Editor Elite",
    description: "For serious editors building recurring income.",
    billingPrices: {
      monthly: 2999,
      yearly: 28790,
    },
    stripeProductId: "prod_placeholder_editor_elite",
    stripePriceIds: {
      monthly: "price_placeholder_editor_elite_monthly",
      yearly: "price_placeholder_editor_elite_yearly",
    },
    features: [
      "Everything in Editor Pro",
      "Homepage spotlight eligibility",
      "Priority profile review",
      "Advanced profile customization",
      "Discounted Portfolio Review",
      "Highest featured placement priority",
    ],
  },
]);

export const ONE_TIME_PRODUCTS = Object.freeze([
  {
    id: "portfolio_review",
    audience: "editor",
    name: "Portfolio Review",
    productType: "manual_service",
    amount: 2900,
    currency: "usd",
    stripeProductId: "prod_placeholder_portfolio_review",
    stripePriceId: "price_placeholder_portfolio_review",
    serviceRequestType: "portfolio_review",
  },
  {
    id: "creator_channel_review",
    audience: "creator",
    name: "Creator Channel Review",
    productType: "manual_service",
    amount: 4900,
    currency: "usd",
    stripeProductId: "prod_placeholder_creator_channel_review",
    stripePriceId: "price_placeholder_creator_channel_review",
    serviceRequestType: "creator_channel_review",
  },
  {
    id: "verified_portfolio_review",
    audience: "editor",
    name: "Verified Portfolio",
    productType: "verification",
    amount: 1500,
    currency: "usd",
    stripeProductId: "prod_placeholder_verified_portfolio_review",
    stripePriceId: "price_placeholder_verified_portfolio_review",
    serviceRequestType: "verified_portfolio_review",
    grantsEntitlementOnApproval: "verifiedPortfolioBadge",
  },
  {
    id: "featured_spotlight_week",
    audience: "editor",
    name: "Featured Spotlight",
    productType: "temporary_boost",
    amount: 1000,
    currency: "usd",
    stripeProductId: "prod_placeholder_featured_spotlight_week",
    stripePriceId: "price_placeholder_featured_spotlight_week",
    durationDays: 7,
    grantsVisibilityBoost: "featuredSpotlightUntil",
  },
]);

const ALL_PLANS = [...CREATOR_PLANS, ...EDITOR_PLANS];
const ACTIVE_SUBSCRIPTION_STATUSES = new Set([
  SUBSCRIPTION_STATUSES.active,
  SUBSCRIPTION_STATUSES.trialing,
]);

function getUserSubscription(userData = {}) {
  return (
    userData?.subscription ||
    userData?.profileInfo?.subscription ||
    userData?.profile?.subscription ||
    userData?.profile?.profileInfo?.subscription ||
    null
  );
}

function normalizeRole(role) {
  const value = String(role || "").trim().toLowerCase();
  if (value === "creator" || value === "creators") return "creator";
  if (value === "editor" || value === "editors") return "editor";
  return null;
}

function getUserRole(userData) {
  const subscription = getUserSubscription(userData);

  return normalizeRole(
    userData?.profileInfo?.role ||
      userData?.role ||
      userData?.profile?.role ||
      userData?.profile?.profileInfo?.role ||
      subscription?.audience
  );
}

function normalizePlanId(value) {
  const normalized = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

  if (!normalized) return null;

  const directMatch = getPlanById(normalized);
  if (directMatch) return directMatch.id;

  const nameMatch = ALL_PLANS.find(
    (plan) =>
      plan.name
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "_")
        .replace(/^_+|_+$/g, "") === normalized
  );

  return nameMatch?.id || null;
}

function getActivePlanId(userData) {
  const subscription = getUserSubscription(userData);
  const planId = normalizePlanId(
    subscription?.planId ||
      subscription?.planName ||
      userData?.planId ||
      userData?.planName
  );
  if (!planId) return null;
  const status = String(subscription?.status || "").trim().toLowerCase();
  if (!ACTIVE_SUBSCRIPTION_STATUSES.has(status)) return null;
  return planId;
}

export function mergeEntitlements(base = {}, overrides = {}) {
  return {
    ...base,
    ...overrides,
  };
}

export function getEntitlementsForPlan(planId) {
  return mergeEntitlements(DEFAULT_ENTITLEMENTS, PLAN_ENTITLEMENTS[planId] || {});
}

export function getDefaultEntitlementsForRole(role) {
  const normalizedRole = normalizeRole(role);
  const freePlanId = normalizedRole === "editor" ? "free_editor" : "free_creator";
  return getEntitlementsForPlan(freePlanId);
}

export function getPlanById(planId) {
  return ALL_PLANS.find((plan) => plan.id === planId) || null;
}

export function getUserSubscriptionSummary(userData = {}) {
  const subscription = getUserSubscription(userData);
  const planId = normalizePlanId(
    subscription?.planId ||
      subscription?.planName ||
      userData?.planId ||
      userData?.planName
  );
  const status = String(subscription?.status || "none").trim().toLowerCase() || "none";

  return {
    audience: getUserRole(userData),
    billingInterval: subscription?.billingInterval || null,
    cancelAtPeriodEnd: Boolean(subscription?.cancelAtPeriodEnd),
    plan: getPlanById(planId),
    planId,
    status,
    subscription,
  };
}

export function getPlansForRole(role) {
  const normalizedRole = normalizeRole(role);
  if (normalizedRole === "creator") return CREATOR_PLANS;
  if (normalizedRole === "editor") return EDITOR_PLANS;
  return [];
}

export function isPlanAllowedForRole(planId, role) {
  const plan = getPlanById(planId);
  const normalizedRole = normalizeRole(role);
  return Boolean(plan && normalizedRole && plan.audience === normalizedRole);
}

export function getUserEntitlements(userData = {}) {
  const role = getUserRole(userData);
  const activePlanId = getActivePlanId(userData);
  const baseEntitlements = getDefaultEntitlementsForRole(role);
  const planEntitlements = activePlanId ? PLAN_ENTITLEMENTS[activePlanId] || {} : {};

  return mergeEntitlements(
    mergeEntitlements(baseEntitlements, planEntitlements),
    mergeEntitlements(
      userData?.entitlements || userData?.profileInfo?.entitlements || {},
      userData?.publicPresentation || {}
    )
  );
}

export function userHasEntitlement(userData, key) {
  if (!key) return false;
  return Boolean(getUserEntitlements(userData)[key]);
}

export function getPortfolioVideoLimit(userData) {
  const limit = getUserEntitlements(userData).portfolioVideoLimit;
  return Number.isFinite(limit) ? limit : DEFAULT_ENTITLEMENTS.portfolioVideoLimit;
}
